import { useCallback, useContext, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { BaseContentBox } from '@pokemon/design.ui.containers.base-content-box'
import { AuthContextValue, AuthContext } from '../../utils/auth/context'
import { Typography } from '@pokemon/design-v2.typography'
import { Button } from '@pokemon/design-v2.button'
import { useNavigate } from 'react-router'
import { Radio } from '@pokemon/design-v2.radio-button'
import { Field, Form } from 'react-final-form'
import { LeaderboardRadioGroup } from '../../components/leaderboard_radio_group'
import { LoadingContainer, LoadingContext, LoadingContextValue } from '@pokemon/design.ui.loading-spinner'
import { RadioGroup } from '@pokemon/design-v2.radio-group'
import { Alert } from '@pokemon/design-v2.alert'
import { ChangeProfilePayload, changeProfile } from '../../utils/tamApi'
import { Callout } from '@pokemon/design.ui.containers.callout'
import { Stack } from '@pokemon/design-v2.stack'
import { Box } from '@mui/system'
import { countryCodeReplacementString, localizeLink } from '@tpci/i18next-ext'

interface PlaySettingsFormValues {
  player_rankings_opt_in: string
  player_rankings_display?: string
}

export interface PlaySettingsPageProps {
  setError: (text?: string) => void,
}

export function PlaySettingsPage (props: PlaySettingsPageProps) {
  const { setError } = props
  const { accessToken, isChildView, loggedUserProfile, selectedAccount } = useContext<AuthContextValue>(AuthContext)
  const { setLoading } = useContext<LoadingContextValue>(LoadingContext)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const playPointsLink = localizeLink(i18n.language, `https://www.pokemon.com/${countryCodeReplacementString}/play-pokemon/about/play-points/`)
  const championshipPointsLink = localizeLink(i18n.language, `https://www.pokemon.com/${countryCodeReplacementString}/play-pokemon/pokemon-events/pokemon-tournaments/earn-championship-points-tcg/`)

  const [showSuccess, setShowSuccess] = useState(false)

  const hasPlayerId = Boolean(loggedUserProfile?.player_id)
  const missingScreenName = !loggedUserProfile?.screen_name
  const missingAddress = !loggedUserProfile?.address?.address1
  const missingName = !loggedUserProfile?.first_name || !loggedUserProfile?.last_name

  const optInFieldProps = useMemo(() => ({
    type: 'radio',
    name: 'player_rankings_opt_in',
    validate: (value: string) => {
      return value === 'true' && (missingScreenName || missingAddress || missingName)
    }
  }), [missingAddress, missingScreenName, missingName])
  const validateKey = `${missingScreenName}${missingAddress}${missingName}`

  const submitOptInSettings = useCallback(async (values: PlaySettingsFormValues) => {
    const optIn = values.player_rankings_opt_in === 'true'
    const changes: ChangeProfilePayload = {
      player_rankings_opt_in: optIn,
      player_rankings_display: optIn ? values.player_rankings_display : undefined
    }
    try {
      setLoading(true)
      await changeProfile(accessToken!.token, changes, isChildView ? selectedAccount?.guid : undefined)
      setShowSuccess(true)
    } catch (err) {
      setError()
    } finally {
      setLoading(false)
    }
  }, [accessToken, isChildView, setError, selectedAccount?.guid, setLoading])

  return (
    <LoadingContainer altText={t('loading_spinner', 'Loading...')}>
      {!hasPlayerId &&
        <BaseContentBox customWidth={700} autoSizeBox={true}
          title={<Typography variant="h5" component="h1">{t('play_pokemon_create_account_title', 'Create a Play! Pokémon Account')}</Typography>}>
          <div style={{ textAlign: 'center' }}>
            <img alt='' aria-hidden='true' style={{ maxWidth: '100%', height: 'auto', marginBottom: 32 }} src={'https://static.pokemon.com/images/play_pokemon_logo.png'} />
          </div>
          <Typography variant='body1'>{t('play_pokemon_create_account_intro', 'Discover the Pokémon TCG and video game community through the Play! Pokémon program. There are Play! Pokémon events for every player, whether you’re new to Pokémon organized play or practicing to become the Pokémon World Champion! The Play! Pokémon program is your opportunity to:')}</Typography>
          <Typography component='ul' style={{ marginTop: 32 }}>
            <Typography variant='body1' component='li'>{t('play_pokemon_create_account_feature1', 'Meet Pokémon fans in your community')}</Typography>
            <Typography variant='body1' component='li'>{t('play_pokemon_create_account_feature2', 'Win prizes, Travel Awards, and more')}</Typography>
            <Typography variant='body1' component='li'>{t('play_pokemon_create_account_feature3', 'Focus on fun')}</Typography>
            <Typography variant='body1' component='li'>{t('play_pokemon_create_account_feature4', 'Aim for higher goals for skilled players')}</Typography>
            <Typography variant='body1' component='li'>{t('play_pokemon_create_account_feature5', 'Play in events that take place all year')}</Typography>
          </Typography>
          <Button disableRipple data-testid="play-pokemon-create-account-start" text={t('play_pokemon_create_account_start_button', 'GET STARTED')} style={{ width: '100%', marginTop: 32 }} onClick={() => navigate('/play-pokemon/name')} />
        </BaseContentBox>
      }
      {hasPlayerId &&
        <Form
          onSubmit={submitOptInSettings}
          initialValues={{
            player_rankings_opt_in: loggedUserProfile?.player_rankings_opt_in ? 'true' : 'false',
            player_rankings_display: loggedUserProfile?.player_rankings_display
          }}
          render={({ handleSubmit, submitting, pristine, values }) => (
            <BaseContentBox customWidth={700} autoSizeBox={true}
              title={<Typography variant="h5" component="h1">{t('play_pokemon_settings_title', 'Play! Pokémon Settings')}</Typography>}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {showSuccess
                    ? <Callout data-testid="play-success" variant='success'>{t('profile_updated', 'Profile successfully updated!')}</Callout>
                    : undefined}
                  <Box>
                    <RadioGroup>
                      <Stack spacing={2}>
                        <Typography variant='h5'>{t('opt_in', 'Opt-in')}</Typography>
                        <Field
                          {...optInFieldProps}
                          value="true"
                          key={`true-${validateKey}`}
                        >
                          {({ input, meta }) => (
                            <>
                              {meta.error && missingScreenName &&
                                <Alert data-testid="play-missing-screen-name-error" variant="outlined" severity="error">
                                  {t('play_opt_in_missing_screen_name', 'You must set a screen name to participate in Play! Pokémon Programs.')}
                                </Alert>
                              }
                              {meta.error && missingAddress &&
                                <Alert data-testid="play-missing-address-error" variant="outlined" severity="error">
                                  {t('play_opt_in_missing_address', 'You must set your address to participate in Play! Pokémon Programs.')}
                                </Alert>
                              }
                              {meta.error && missingName &&
                                <Alert data-testid="play-missing-name-error" variant="outlined" severity="error">
                                  {t('play_opt_in_missing_name', 'You must set your full name to participate in Play! Pokémon Programs.')}
                                </Alert>
                              }
                              <Radio
                                {...input}
                                data-testid="play-opt-in-yes"
                                label={
                                  <Trans
                                    t={t}
                                    i18nKey='play_opt_in_yes'
                                    defaults='Yes! I want to participate in all available Play! Pokémon Programs including <championshipPointsLink>Championship Points</championshipPointsLink> and <playPointsLink>Play! Points</playPointsLink>. By selecting this preference, and providing valid contact information, I am eligible to earn special prizes such as travel awards, scholarships, and event invitations.'
                                    components={{
                                      playPointsLink: <a target='_blank' rel="noreferrer" href={playPointsLink} />,
                                      championshipPointsLink: <a target='_blank' rel="noreferrer" href={championshipPointsLink} />
                                    }}
                                  />
                                }
                              />
                            </>
                          )}
                        </Field>

                        <Field
                          {...optInFieldProps}
                          value="false"
                          key={`false-${validateKey}`}
                        >
                          {({ input }) => (
                            <Radio
                              {...input}
                              data-testid="play-opt-in-no"
                              label={t('play_opt_in_no', 'No. I do not wish to participate in any Play! Pokémon program and I understand that I will NOT be eligible to earn special prizes such as travel awards, scholarships, and event invitations.')}
                            />
                          )}
                        </Field>
                      </Stack>
                    </RadioGroup>
                  </Box>
                  <Box>
                    <Stack spacing={2}>
                      <Typography variant='h5'>{t('leaderboard_display_name', 'Leaderboard Display Name')}</Typography>
                      <Typography variant='body2'>{t('leaderboard_display_message', 'Your name on the leaderboard that will be displayed publicly.')}</Typography>
                      <LeaderboardRadioGroup disabled={values.player_rankings_opt_in !== 'true'} />
                    </Stack>
                  </Box>
                  <Button type="submit" disabled={submitting || pristine} disableRipple data-testid="play-save-button" text={t('save_button', 'Save')}/>
                </Stack>
              </form>
            </BaseContentBox>
          )}
        />
      }
    </LoadingContainer>
  )
}
